import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from '../../util';
import useDocumentMeta from '../../utils/useDocumentMeta';
import { useAppContext } from '../../context/AppContext';
import axios from 'axios';
import dayjs from 'dayjs';
import StatCard from '../../components/StatCard/StatCard';
import BarChartCard from '../../components/BarChartCard/BarChartCard';
import PieChartCard from '../../components/PieChartCard/PieChartCard';
import SegmentedBarCard from '../../components/SegmentedBarCard/SegmentedBarCard';
import UpcomingAppointmentsCard from '../../components/UpcomingAppointmentsCard/UpcomingAppointmentsCard';
import CalendarEventModal from '../../components/Modals/NewCalendarEventModal/CalendarEventModal';
import { useNotification } from '../../components/notifications/useNotification';
import TourButton from '../../components/TourButton/TourButton';
import { getHomePageSteps } from '../../tours/homePage';
import './HomePage.css';
import { capitalizeFirst } from '../../utils/functions';

/**
 * Fixed age-range buckets used to group clients by age in the demographics
 * bar chart. The order determines the x-axis sequence.
 *
 * @constant {string[]}
 */
const AGE_BUCKETS = ['<18', '18-25', '26-35', '36-45', '46-55', '56-65', '65+'];

/**
 * Fixed caloric-range buckets (kcal/day) used to group diets in the caloric
 * distribution bar chart. The order determines the x-axis sequence.
 *
 * @constant {string[]}
 */
const CALORIC_BUCKETS = [
    '<800', '800-1100', '1100-1400', '1400-1700', '1700-2000',
    '2000-2300', '2300-2600', '2600-2900', '2900-3200',
    '3200-3500', '3500-3800', '3800-4100', '>4100',
];

/**
 * HomePage component.
 *
 * Responsive redesign of the nutritionist dashboard. Mirrors the data model
 * of the legacy {@link Home} component while adopting a new layout and style
 * system that adapts from mobile to desktop viewports.
 *
 * All chart data is fetched from the same API endpoints as the legacy page;
 * the UI is built with plain React + scoped CSS (`.nhp-*` class prefix) and
 * no third-party chart libraries.
 *
 * @component
 * @returns {JSX.Element|null} The rendered dashboard, or `null` while the
 *   component is being built out step by step.
 */
const HomePage = () => {
    const { t } = useTranslation();
    const { showSuccess, showError } = useNotification();
    const { appColors } = useAppContext();

    /** @type {{ dietist: object, currentLanguage: string }} */
    const { dietist, currentLanguage } = useAppContext();

    const tourSteps = useMemo(() => getHomePageSteps(t), [t]);

    /**
     * Translated month names, ordered January–December, used as x-axis labels
     * in all month-based bar charts.
     *
     * @type {string[]}
     */
    const months = [
        t('January'), t('February'), t('March'), t('April'),
        t('May'), t('June'), t('July'), t('August'),
        t('September'), t('October'), t('November'), t('December'),
    ];
    /**
     * Descending list of years from the current year back to 2022, used to
     * populate year-filter dropdowns in the chart cards.
     *
     * @type {string[]}
     */
    const yearOptions = Array.from(
        { length: new Date().getFullYear() - 2021 },
        (_, i) => (new Date().getFullYear() - i).toString()
    );
    /* ── Clients bar chart ─────────────────────────────────────────────── */

    /**
     * Per-month count of newly registered clients for the selected year.
     * Array index 0 = January, 11 = December.
     *
     * @type {[number[], React.Dispatch<React.SetStateAction<number[]>>]}
     */
    const [newClientsByMonth, setNewClientsByMonth] = useState([]);

    /**
     * Per-month running total of all clients for the selected year.
     *
     * @type {[number[], React.Dispatch<React.SetStateAction<number[]>>]}
     */
    const [totalClientsByMonth, setTotalClientsByMonth] = useState([]);

    /**
     * Index of the currently active clients bar-chart view.
     * `0` → new clients, `1` → total clients.
     *
     * @type {[number, React.Dispatch<React.SetStateAction<number>>]}
     */
    const [clientsBarChartType, setClientsBarChartType] = useState(0);

    /**
     * Four-digit year string used to filter the clients bar chart.
     * Defaults to the current calendar year.
     *
     * @type {[string, React.Dispatch<React.SetStateAction<string>>]}
     */
    const [clientsBarChartYearFilter, setClientsBarChartYearFilter] = useState(
        new Date().getFullYear().toString()
    );

    /**
     * API query-parameter identifiers for each clients bar-chart view,
     * indexed in sync with `clientsBarChartType`.
     *
     * @type {string[]}
     */
    const clientsBarChartTypes = ['newClients', 'totalClients'];

    /**
     * Translated display titles for each clients bar-chart view,
     * indexed in sync with `clientsBarChartType`.
     *
     * @type {string[]}
     */
    const clientsBarChartTitles = [t('new-clients'), t('total-clients')];

    /* ── Clients pie chart ─────────────────────────────────────────────── */

    /**
     * Counts of active, recently-inactive, and inactive clients.
     * Shape: `{ active: number, "recently-inactive": number, inactive: number }`.
     *
     * @type {[object, React.Dispatch<React.SetStateAction<object>>]}
     */
    const [activeAndUnactiveClients, setActiveAndUnactiveClients] = useState({});

    /**
     * Index of the currently active clients pie-chart view.
     * Currently only `0` (active/inactive split) is supported.
     *
     * @type {[number, React.Dispatch<React.SetStateAction<number>>]}
     */
    const [clientsPieChartType, setClientsPieChartType] = useState(0);

    /**
     * API query-parameter identifiers for each clients pie-chart view.
     *
     * @type {string[]}
     */
    const clientsPieChartTypes = ['activeClients'];

    /**
     * Translated display titles for each clients pie-chart view.
     *
     * @type {string[]}
     */
    const clientsPieChartTitles = [t('active-clients')];

    /* ── Intolerances bar chart ────────────────────────────────────────── */

    /**
     * Full list of food-intolerance objects fetched from the API.
     * Each object contains at minimum `_id` and a `name` map keyed by locale.
     *
     * @type {[object[], React.Dispatch<React.SetStateAction<object[]>>]}
     */
    const [intolerances, setIntolerances] = useState([]);

    /**
     * Map from intolerance `_id` to the number of clients who have it.
     * Shape: `{ [intoleranceId: string]: number }`.
     *
     * @type {[object, React.Dispatch<React.SetStateAction<object>>]}
     */
    const [intolerancesCount, setIntolerancesCount] = useState({});

    /* ── Illnesses bar chart ────────────────────────────────────────────── */

    const [illnesses, setIllnesses] = useState([]);
    const [illnessesCount, setIllnessesCount] = useState({});

    /* ── Age distribution bar chart ────────────────────────────────────── */

    /**
     * Map from age-bucket label (e.g. `'18-25'`) to the number of clients
     * whose current age falls within that range.
     * Shape: `{ [bucket: string]: number }`.
     *
     * @type {[object, React.Dispatch<React.SetStateAction<object>>]}
     */
    const [clientsByAgeRange, setClientsByAgeRange] = useState({});

    /* ── Gender split pie chart ────────────────────────────────────────── */

    /**
     * Map from gender key (`'male'` | `'female'`) to client count.
     * Shape: `{ male: number, female: number }`.
     *
     * @type {[object, React.Dispatch<React.SetStateAction<object>>]}
     */
    const [clientsByGender, setClientsByGender] = useState({});

    /* ── Diets generated per month bar chart ───────────────────────────── */

    /**
     * Per-month count of diets generated for the selected year.
     * Array index 0 = January, 11 = December.
     *
     * @type {[number[], React.Dispatch<React.SetStateAction<number[]>>]}
     */
    const [dietsGeneratedPerMonth, setDietsGeneratedPerMonth] = useState([]);

    /**
     * Four-digit year string used to filter the diets-per-month bar chart.
     * Defaults to the current calendar year.
     *
     * @type {[string, React.Dispatch<React.SetStateAction<string>>]}
     */
    const [dietsBarChartYearFilter, setDietsBarChartYearFilter] = useState(
        new Date().getFullYear().toString()
    );

    /* ── Average diet duration per month bar chart ─────────────────────── */

    /**
     * Per-month average diet duration in weeks for the selected year.
     * Array index 0 = January, 11 = December. `null` entries indicate months
     * with no data.
     *
     * @type {[(number|null)[], React.Dispatch<React.SetStateAction<(number|null)[]>>]}
     */
    const [avgDietDurationByMonth, setAvgDietDurationByMonth] = useState([]);

    /**
     * Four-digit year string used to filter the average diet-duration bar chart.
     * Defaults to the current calendar year.
     *
     * @type {[string, React.Dispatch<React.SetStateAction<string>>]}
     */
    const [avgDietDurationYearFilter, setAvgDietDurationYearFilter] = useState(
        new Date().getFullYear().toString()
    );

    /* ── Diets by caloric range bar chart ──────────────────────────────── */

    /**
     * Map from caloric-bucket label (e.g. `'1400-1700'`) to the number of
     * diets whose total daily calories fall within that range.
     * Shape: `{ [bucket: string]: number }`.
     *
     * @type {[object, React.Dispatch<React.SetStateAction<object>>]}
     */
    const [dietsByCaloricRange, setDietsByCaloricRange] = useState({});

    /* ── Stat cards ────────────────────────────────────────────────────── */

    /**
     * Data for the "New Clients" stat card.
     * `value`   — count of new clients registered this month.
     * `delta`   — percentage change vs. the previous month (`null` if unavailable).
     * `loading` — `true` while the API call is in-flight.
     *
     * @type {[{value: number, delta: number|null, loading: boolean}, React.Dispatch]}
     */
    const [newClientsStats, setNewClientsStats] = useState({ value: 0, delta: null, loading: true });

    /**
     * Data for the "Total Clients" stat card.
     * `value`   — total number of registered clients.
     * `delta`   — percentage change vs. the previous month (`null` if unavailable).
     * `loading` — `true` while the API call is in-flight.
     *
     * @type {[{value: number, delta: number|null, loading: boolean}, React.Dispatch]}
     */
    const [totalClientsStats, setTotalClientsStats] = useState({ value: 0, delta: null, loading: true });

    /**
     * Data for the "Appointments" stat card.
     * `value`   — count of appointments scheduled in the current week.
     * `delta`   — percentage change vs. the previous week (`null` if unavailable).
     * `loading` — `true` while the API call is in-flight.
     *
     * @type {[{value: number, delta: number|null, loading: boolean}, React.Dispatch]}
     */
    const [appointmentsStats, setAppointmentsStats] = useState({ value: 0, delta: null, loading: true });

    /**
     * Data for the "Retention Rate" stat card.
     * `value`   — retention rate as a percentage string (e.g. `"82%"`).
     * `delta`   — percentage-point change vs. the previous month (`null` if unavailable).
     * `loading` — `true` while the API call is in-flight.
     *
     * @type {[{value: string, delta: number|null, loading: boolean}, React.Dispatch]}
     */
    const [retentionStats, setRetentionStats] = useState({ value: '0%', delta: null, loading: true });

    /**
     * Whether the clients bar chart is currently fetching data.
     * Shows a shimmer skeleton inside BarChartCard while `true`.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [clientsBarChartLoading, setClientsBarChartLoading] = useState(true);

    /**
     * Whether the active-clients pie chart is currently fetching data.
     * Shows a shimmer skeleton inside PieChartCard while `true`.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [activeClientsLoading, setActiveClientsLoading] = useState(true);

    /**
     * Four-digit year string used to filter the new-clients-per-month bar chart.
     * Defaults to the current calendar year.
     *
     * @type {[string, React.Dispatch<React.SetStateAction<string>>]}
     */
    const [newClientsBarChartYearFilter, setNewClientsBarChartYearFilter] = useState(
        new Date().getFullYear().toString()
    );

    /**
     * Whether the new-clients bar chart is currently fetching data.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [newClientsBarChartLoading, setNewClientsBarChartLoading] = useState(true);

    /**
     * Whether the age-distribution and gender-split charts are fetching data.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [ageGenderLoading, setAgeGenderLoading] = useState(true);

    /**
     * Whether the intolerances bar chart is fetching data.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [intolerancesLoading, setIntolerancesLoading] = useState(true);

    /**
     * Whether the illnesses bar chart is fetching data.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [illnessesLoading, setIllnessesLoading] = useState(true);

    /**
     * Whether the diets-generated-per-month bar chart is fetching data.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [dietsBarChartLoading, setDietsBarChartLoading] = useState(true);

    /**
     * Whether the average-diet-duration bar chart is fetching data.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [avgDietDurationLoading, setAvgDietDurationLoading] = useState(true);

    /**
     * Whether the diets-by-caloric-range bar chart is fetching data.
     *
     * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
     */
    const [dietsCaloricLoading, setDietsCaloricLoading] = useState(true);

    /* ── Upcoming appointments ────────────────────────────────────────────── */

    const [upcomingEvents,  setUpcomingEvents]  = useState([]);
    const [upcomingLoading, setUpcomingLoading] = useState(true);
    const [editEventOpen,   setEditEventOpen]   = useState(false);
    const [editEventData,   setEditEventData]   = useState(null);

    /* ── Upcoming events fetch & handlers ─────────────────────────────── */

    const fetchUpcomingEvents = async () => {
        try {
            const now = dayjs();
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/events`,
                {
                    params: {
                        startDate: now.toISOString(),
                        endDate:   now.add(30, 'day').toISOString(),
                    },
                    withCredentials: true,
                }
            );
            const sorted = [...(Array.isArray(data) ? data : [])].sort(
                (a, b) => new Date(a.startDate) - new Date(b.startDate)
            );
            setUpcomingEvents(sorted.slice(0, 5));
        } catch (err) {
            console.error('Error fetching upcoming events:', err);
        } finally {
            setUpcomingLoading(false);
        }
    };

    const handleEditEvent = (event) => {
        setEditEventData(event);
        setEditEventOpen(true);
    };

    const handleDeleteEvent = async (event) => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/calendarEvents/${event._id}`,
                { withCredentials: true }
            );
            showSuccess(t('event-deleted'));
            await fetchUpcomingEvents();
        } catch (err) {
            console.error('Delete event error:', err);
            showError(t('event-not-deleted'));
        }
    };

    useDocumentMeta(null, null);

    /**
     * On mount: verifies the session token and fetches all stat-card data.
     * Redirects to `/login` if the token is invalid.
     */
    useEffect(() => {
        if (!dietist) return;

        /**
         * Fetches the data for all four stat cards in parallel and writes each
         * result into its corresponding state slice.
         *
         * API endpoints used:
         * - **New clients**   `GET /clients?newClientsByMonth=true&yearFilter=<year>`
         *   The current and previous month counts are derived from the returned array.
         * - **Total clients** `GET /clients?totalCount=true`
         *   Expects `{ current: number, previous: number }`.
         * - **Appointments**  `GET /calendarEvents?weeklyCount=true&language=<currentLanguage>` // current language is needed to determine the week start day (e.g. Sunday vs Monday)
         *   Expects `{ current: number, previous: number }`.
         * - **Retention rate** `GET /clients?retentionStats=true`
         *   Expects `{ current: number, previous: number }` where values are percentages.
         *
         * @async
         */
        const fetchStatCardsData = async () => {
            const id             = dietist._id;
            const currentYear    = new Date().getFullYear();
            const currentMonthIdx = new Date().getMonth(); // 0-indexed

            try {
                const [newClientsRes, totalRes, apptRes, retentionRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                        params: { dietist: id, newClientsByMonth: true, yearFilter: currentYear },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                        params: { dietist: id, totalCount: true },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/calendarEvents`, {
                        params: { dietist: id, weeklyCount: true, language: currentLanguage },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                        params: { dietist: id, retentionStats: true },
                        withCredentials: true,
                    }),
                ]);

                // ── New clients this month vs previous month ───────────────────
                const newByMonth   = newClientsRes.data.newClientsByMonth ?? [];
                const newThisMonth = newByMonth[currentMonthIdx] ?? 0;
                const newLastMonth = currentMonthIdx > 0
                    ? (newByMonth[currentMonthIdx - 1] ?? 0)
                    : null;
                const newDelta = newLastMonth !== null && newLastMonth > 0
                    ? Math.round(((newThisMonth - newLastMonth) / newLastMonth) * 100)
                    : null;
                setNewClientsStats({ value: newThisMonth, delta: newDelta, loading: false });
                setNewClientsByMonth(newByMonth);
                setNewClientsBarChartLoading(false);

                // ── Total clients now vs previous month ────────────────────────
                const totalCurrent  = totalRes.data.totalCount.totalCount  ?? 0;
                const totalPrevious = totalRes.data.totalCount.totalCountLastMonth ?? 0;
                const totalDelta = totalPrevious > 0
                    ? Math.round(((totalCurrent - totalPrevious) / totalPrevious) * 100)
                    : null;
                setTotalClientsStats({ value: totalCurrent, delta: totalDelta, loading: false });

                // ── Appointments this week vs last week ────────────────────────
                const apptCurrent  = apptRes.data.weeklyCount.current  ?? 0;
                const apptPrevious = apptRes.data.weeklyCount.previous ?? 0;
                const apptDelta = apptPrevious > 0
                    ? Math.round(((apptCurrent - apptPrevious) / apptPrevious) * 100)
                    : null;
                setAppointmentsStats({ value: apptCurrent, delta: apptDelta, loading: false });

                // ── Retention rate this month vs previous month ────────────────
                // delta is expressed in percentage points (e.g. 80 → 82 = +2 pp)
                const retCurrent  = retentionRes.data.retentionStats.current  ?? 0;
                const retPrevious = retentionRes.data.retentionStats.lastMonth ?? null;
                const retDelta = retPrevious !== null
                    ? Math.round(retCurrent - retPrevious)
                    : null;
                setRetentionStats({ value: `${retCurrent}%`, delta: retDelta, loading: false });

            } catch (err) {
                console.error('Error fetching stat card data:', err);
                // Disable loading spinners even on error so the UI is not stuck
                setNewClientsStats(prev   => ({ ...prev, loading: false }));
                setTotalClientsStats(prev => ({ ...prev, loading: false }));
                setAppointmentsStats(prev => ({ ...prev, loading: false }));
                setRetentionStats(prev    => ({ ...prev, loading: false }));
                setNewClientsBarChartLoading(false);
            }
        };

        fetchStatCardsData();

        /**
         * Fetches the initial data for the second dashboard row in parallel:
         * - Total clients per month for the bar chart.
         * - Active / inactive / recently-inactive client split for the pie chart.
         *
         * @async
         */
        const fetchChartsData = async () => {
            const id          = dietist._id;
            const currentYear = new Date().getFullYear();
            try {
                const [totalByMonthRes, activeClientsRes, ageRangeRes, genderRes, intolerancesRes, intolerancesCountRes, illnessesRes, illnessesCountRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                        params: { dietist: id, totalClientsByMonth: true, yearFilter: currentYear },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                        params: { dietist: id, activeAndUnactiveClients: true },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                        params: { dietist: id, clientsByAgeRange: true },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                        params: { dietist: id, clientsByGender: true },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/food-intolerances`, {
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients?dietist=${dietist._id}&clientsByIntolerances=true`, {
                        params: { dietist: id, intolerancesCount: true },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/illnesses`, {
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                        params: { dietist: id, clientsByIllnesses: true },
                        withCredentials: true,
                    }),
                ]);
                setTotalClientsByMonth(totalByMonthRes.data.totalClientsByMonth ?? []);
                setActiveAndUnactiveClients(activeClientsRes.data.activeAndUnactiveClients ?? {});
                setClientsByAgeRange(ageRangeRes.data.clientsByAgeRange ?? {});
                setClientsByGender(genderRes.data.clientsByGender ?? {});
                setIntolerances(Array.isArray(intolerancesRes.data) ? intolerancesRes.data : []);
                setIntolerancesCount(intolerancesCountRes.data.clientsByIntolerances ?? {});
                setIllnesses(Array.isArray(illnessesRes.data) ? illnessesRes.data : []);
                setIllnessesCount(illnessesCountRes.data.clientsByIllnesses ?? {});
            } catch (err) {
                console.error('Error fetching charts data:', err);
            } finally {
                setClientsBarChartLoading(false);
                setActiveClientsLoading(false);
                setAgeGenderLoading(false);
                setIntolerancesLoading(false);
                setIllnessesLoading(false);
            }
        };

        fetchChartsData();

        /**
         * Fetches diet chart data in parallel:
         * - Diets generated per month for the selected year.
         * - Average diet duration per month for the selected year.
         * - Diets grouped by caloric range (all time).
         *
         * @async
         */
        const fetchDietsData = async () => {
            const id          = dietist._id;
            const currentYear = new Date().getFullYear();
            try {
                const [dietsPerMonthRes, avgDurationRes, caloricRangeRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/diets`, {
                        params: { dietist: id, dietsGeneratedPerMonth: true, yearFilter: currentYear },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/diets`, {
                        params: { dietist: id, avgDietDurationByMonth: true, yearFilter: currentYear },
                        withCredentials: true,
                    }),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/diets`, {
                        params: { dietist: id, dietsByCaloricRange: true },
                        withCredentials: true,
                    }),
                ]);
                setDietsGeneratedPerMonth(dietsPerMonthRes.data.dietsGeneratedPerMonth ?? []);
                setAvgDietDurationByMonth(avgDurationRes.data.avgDietDurationByMonth ?? []);
                setDietsByCaloricRange(caloricRangeRes.data.dietsByCaloricRange ?? {});
            } catch (err) {
                console.error('Error fetching diets chart data:', err);
            } finally {
                setDietsBarChartLoading(false);
                setAvgDietDurationLoading(false);
                setDietsCaloricLoading(false);
            }
        };

        fetchDietsData();
        fetchUpcomingEvents();
    }, [dietist]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Chart handlers ──────────────────────────────────────────────────── */

    /**
     * Handles year-filter changes on the total-clients bar chart.
     * Persists the selected year and re-fetches data with the new filter.
     *
     * @async
     * @param {React.ChangeEvent} e - Change event from the MUI Select component.
     */
    const handleClientsBarChartYearFilter = async (e) => {
        const year = e.target.value;
        setClientsBarChartYearFilter(year);
        setClientsBarChartLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                params: { dietist: dietist._id, totalClientsByMonth: true, yearFilter: year },
                withCredentials: true,
            });
            setTotalClientsByMonth(res.data.totalClientsByMonth ?? []);
        } catch (err) {
            console.error('Error updating clients bar chart:', err);
        } finally {
            setClientsBarChartLoading(false);
        }
    };

    /**
     * Handles year-filter changes on the new-clients-per-month bar chart.
     *
     * @async
     * @param {React.ChangeEvent} e - Change event from the year Select.
     */
    const handleNewClientsBarChartYearFilter = async (e) => {
        const year = e.target.value;
        setNewClientsBarChartYearFilter(year);
        setNewClientsBarChartLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/clients`, {
                params: { dietist: dietist._id, newClientsByMonth: true, yearFilter: year },
                withCredentials: true,
            });
            setNewClientsByMonth(res.data.newClientsByMonth ?? []);
        } catch (err) {
            console.error('Error updating new clients bar chart:', err);
        } finally {
            setNewClientsBarChartLoading(false);
        }
    };

    /**
     * Handles year-filter changes on the diets-generated-per-month bar chart.
     *
     * @async
     * @param {React.ChangeEvent} e - Change event from the year Select.
     */
    const handleDietsBarChartYearFilter = async (e) => {
        const year = e.target.value;
        setDietsBarChartYearFilter(year);
        setDietsBarChartLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/diets`, {
                params: { dietist: dietist._id, dietsGeneratedPerMonth: true, yearFilter: year },
                withCredentials: true,
            });
            setDietsGeneratedPerMonth(res.data.dietsGeneratedPerMonth ?? []);
        } catch (err) {
            console.error('Error updating diets bar chart:', err);
        } finally {
            setDietsBarChartLoading(false);
        }
    };

    /**
     * Handles year-filter changes on the average-diet-duration bar chart.
     *
     * @async
     * @param {React.ChangeEvent} e - Change event from the year Select.
     */
    const handleAvgDietDurationYearFilter = async (e) => {
        const year = e.target.value;
        setAvgDietDurationYearFilter(year);
        setAvgDietDurationLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/diets`, {
                params: { dietist: dietist._id, avgDietDurationByMonth: true, yearFilter: year },
                withCredentials: true,
            });
            setAvgDietDurationByMonth(res.data.avgDietDurationByMonth ?? []);
        } catch (err) {
            console.error('Error updating avg diet duration chart:', err);
        } finally {
            setAvgDietDurationLoading(false);
        }
    };

    /* ── Derived chart data ─────────────────────────────────────────────── */

    /**
     * `true` when `activeAndUnactiveClients` contains no usable data,
     * triggering the gray placeholder slice in the pie chart.
     *
     * @type {boolean}
     */
    const pieChartIsEmpty =
        Object.keys(activeAndUnactiveClients).length === 0 ||
        Object.values(activeAndUnactiveClients).every(v => v === 0);

    /**
     * Slice descriptors ready to be consumed by PieChartCard.
     * When empty a single gray placeholder slice is returned instead.
     *
     * @type {Array<{id: number, value: number, label: string, color: string, description: string}>}
     */
    const pieChartData = pieChartIsEmpty
        ? [{ id: 0, value: 100, color: 'lightgray', label: t('errorMessages.no-data-to-display') }]
        : Object.entries(activeAndUnactiveClients).map(([key, value], index) => ({
            id: index,
            value,
            label: capitalize(t(key)),
            color: {
                active:              appColors.secondary,
                inactive:            '#94A3B8',
                'recently-inactive': appColors.tertiary,
            }[key] ?? '#aaaaaa',
            description: {
                active:              t('pie-legend.active-description'),
                inactive:            t('pie-legend.inactive-description'),
                'recently-inactive': t('pie-legend.recently-inactive-description'),
            }[key] ?? '',
        }));

    /**
     * Two-segment data for SegmentedBarCard: active vs combined inactive.
     * "inactive" merges the `inactive` and `recently-inactive` buckets.
     */
    const segmentedBarData = pieChartIsEmpty ? [] : [
        {
            id: 0,
            value: activeAndUnactiveClients['active'] ?? 0,
            label: capitalize(t('actives')),
            color: appColors.secondary,
        },
        {
            id: 1,
            value: (activeAndUnactiveClients['inactive'] ?? 0) + (activeAndUnactiveClients['recently-inactive'] ?? 0),
            label: capitalize(t('inactives')),
            color: '#94A3B8',
        },
    ];

    /** Age distribution: ordered array of counts matching AGE_BUCKETS */
    const ageDistributionData = AGE_BUCKETS.map(bucket => clientsByAgeRange[bucket] ?? 0);

    /** Gender pie chart */
    const genderPieIsEmpty =
        Object.keys(clientsByGender).length === 0 ||
        Object.values(clientsByGender).every(v => v === 0);

    const genderPieData = genderPieIsEmpty
        ? [{ id: 0, value: 100, color: 'lightgray', label: t('errorMessages.no-data-to-display') }]
        : [
            { id: 0, value: clientsByGender.male   ?? 0, label: capitalize(t('Male')),   color: '#60a5fa' },
            { id: 1, value: clientsByGender.female ?? 0, label: capitalize(t('Female')), color: '#f472b6' },
        ];

    /** Age distribution pie chart — uses percentages */
    const agePieTotal = ageDistributionData.reduce((sum, v) => sum + v, 0);
    const agePieIsEmpty = agePieTotal === 0;
    const AGE_COLORS = ['#818cf8', '#a78bfa', '#c084fc', '#e879f9', '#f472b6', '#fb7185', '#fbbf24'];
    const agePieData = agePieIsEmpty
        ? [{ id: 0, value: 100, color: 'lightgray', label: t('errorMessages.no-data-to-display') }]
        : AGE_BUCKETS.map((bucket, i) => ({
            id: i,
            value: clientsByAgeRange[bucket] ?? 0,
            label: bucket,
            color: AGE_COLORS[i % AGE_COLORS.length],
          })).filter(s => s.value > 0);

    /** Intolerances bar chart — x-axis: intolerance names, y-axis: client counts, sorted descending */
    const intolerancesBarEntries = intolerances
        .map(intol => ({
            label: intol.name?.[currentLanguage] ?? intol.name?.['it'] ?? intol._id,
            value: intolerancesCount[intol._id] ?? 0,
        }))
        .filter(e => e.value > 0)
        .sort((a, b) => b.value - a.value);
    const intolerancesBarData   = intolerancesBarEntries.map(e => e.value);
    const intolerancesBarLabels = intolerancesBarEntries.map(e => e.label);

    /** Illnesses bar chart — x-axis: illness names, y-axis: client counts, sorted descending */
    const illnessesBarEntries = illnesses
        .map(illness => ({
            label: illness.name?.[currentLanguage] ?? illness.name?.['it'] ?? illness._id,
            value: illnessesCount[illness._id] ?? 0,
        }))
        .filter(e => e.value > 0)
        .sort((a, b) => b.value - a.value);
    const illnessesBarData   = illnessesBarEntries.map(e => e.value);
    const illnessesBarLabels = illnessesBarEntries.map(e => e.label);

    /** Intolerances pie chart */
    const PIE_PALETTE = ['#fc9f5b', '#f472b6', '#818cf8', '#34d399', '#60a5fa', '#fbbf24', '#a78bfa', '#fb7185', '#4ade80', '#38bdf8'];
    const intolerancePieIsEmpty = intolerancesBarEntries.length === 0;
    const intolerancePieData = intolerancePieIsEmpty
        ? [{ id: 0, value: 100, color: 'lightgray', label: t('errorMessages.no-data-to-display') }]
        : intolerancesBarEntries.map((e, i) => ({
            id: i,
            value: e.value,
            label: e.label,
            color: PIE_PALETTE[i % PIE_PALETTE.length],
          }));

    /** Illnesses pie chart */
    const illnessPieIsEmpty = illnessesBarEntries.length === 0;
    const illnessPieData = illnessPieIsEmpty
        ? [{ id: 0, value: 100, color: 'lightgray', label: t('errorMessages.no-data-to-display') }]
        : illnessesBarEntries.map((e, i) => ({
            id: i,
            value: e.value,
            label: e.label,
            color: PIE_PALETTE[i % PIE_PALETTE.length],
          }));

    /** Diets by caloric range: ordered array of counts matching CALORIC_BUCKETS */
    const dietsCaloricData = CALORIC_BUCKETS.map(bucket => dietsByCaloricRange[bucket] ?? 0);

    return (
        <div id='home-page' className='home-page'>

            <div className='nhp-page-header'>
                <TourButton steps={tourSteps} label={t('tour.tutorial-btn')} />
            </div>

            {/* ── Stat cards row ── */}
            <div className='nhp-stats-row' data-tour="hp-stat-cards">
                <StatCard
                    title={t('new-clients')}
                    icon='person_add'
                    iconColor='green'
                    value={newClientsStats.value}
                    delta={newClientsStats.delta}
                    subText={t('stat-card-new-clients-sub')}
                    loading={newClientsStats.loading}
                />
                <StatCard
                    title={t('total-clients')}
                    icon='group'
                    iconColor='orange'
                    value={totalClientsStats.value}
                    delta={totalClientsStats.delta}
                    subText={
                        totalClientsStats.delta > 0
                            ? t('stat-card-total-clients-sub-growing')
                            : totalClientsStats.delta < 0
                                ? t('stat-card-total-clients-sub-declining')
                                : t('stat-card-total-clients-sub-stable')
                    }
                    loading={totalClientsStats.loading}
                />
                <StatCard
                    title={t('stat-card-appointments-title')}
                    icon='calendar_month'
                    iconColor='blue'
                    value={appointmentsStats.value}
                    delta={appointmentsStats.delta}
                    subText={t('stat-card-appointments-sub')}
                    loading={appointmentsStats.loading}
                />
                <StatCard
                    title={t('stat-card-retention-title')}
                    icon='loop'
                    iconColor='purple'
                    value={retentionStats.value}
                    delta={retentionStats.delta}
                    subText={t('stat-card-retention-sub')}
                    tooltip={t('stat-card-retention-info')}
                    loading={retentionStats.loading}
                />
            </div>

            {/* ── Upcoming appointments row ── */}
            <div data-tour="hp-upcoming-appointments">
                <UpcomingAppointmentsCard
                    title={t('upcoming-appointments')}
                    events={upcomingEvents}
                    loading={upcomingLoading}
                    onEdit={handleEditEvent}
                    onDelete={handleDeleteEvent}
                />
            </div>

            {/* ── Charts row: total clients bar chart (65 %) + active clients pie chart (35 %) ── */}
            <div className='nhp-charts-row' data-tour="hp-clients-charts">
                <BarChartCard
                    title={t('total-clients')}
                    yearOptions={yearOptions}
                    selectedYear={clientsBarChartYearFilter}
                    onYearChange={handleClientsBarChartYearFilter}
                    data={totalClientsByMonth}
                    xAxisLabels={months}
                    yAxisLabel={t('Clients')}
                    noDataMessage={t('errorMessages.no-data-to-display')}
                    loading={clientsBarChartLoading}
                />
                <div className='nhp-right-col'>
                    <PieChartCard
                        title={t('active-clients')}
                        data={pieChartData}
                        isEmpty={pieChartIsEmpty}
                        loading={activeClientsLoading}
                    />
                    <SegmentedBarCard
                        title={t('active-clients')}
                        data={segmentedBarData}
                        isEmpty={pieChartIsEmpty}
                        loading={activeClientsLoading}
                    />
                </div>
            </div>
            {/* ── Charts row: new clients per month ── */}
            <div className='nhp-charts-row' data-tour="hp-new-clients-chart">
                <BarChartCard
                    title={t('new-clients')}
                    yearOptions={yearOptions}
                    selectedYear={newClientsBarChartYearFilter}
                    onYearChange={handleNewClientsBarChartYearFilter}
                    data={newClientsByMonth}
                    xAxisLabels={months}
                    yAxisLabel={t('Clients')}
                    noDataMessage={t('errorMessages.no-data-to-display')}
                    loading={newClientsBarChartLoading}
                />
            </div>

            {/* ── Charts row: age distribution (bar) + gender split (pie) ── */}
            <div className='nhp-charts-row' data-tour="hp-age-gender-charts">
                <BarChartCard
                    title={capitalizeFirst(t('age-distribution-data'))}
                    data={ageDistributionData}
                    xAxisLabels={AGE_BUCKETS}
                    yAxisLabel={t('Clients')}
                    noDataMessage={t('errorMessages.no-data-to-display')}
                    loading={ageGenderLoading}
                />
                <div className='nhp-right-col'>
                    <PieChartCard
                        title={capitalizeFirst(t('gender-split-data'))}
                        data={genderPieData}
                        isEmpty={genderPieIsEmpty}
                        loading={ageGenderLoading}
                    />
                    <PieChartCard
                        title={capitalizeFirst(t('age-distribution-data'))}
                        data={agePieData}
                        isEmpty={agePieIsEmpty}
                        loading={ageGenderLoading}
                    />
                </div>
            </div>

            {/* ── Charts row: intolerances distribution ── */}
            <div className='nhp-charts-row' data-tour="hp-intolerances-chart">
                <BarChartCard
                    title={capitalizeFirst(t('intolerances'))}
                    data={intolerancesBarData}
                    xAxisLabels={intolerancesBarLabels}
                    yAxisLabel={t('Clients')}
                    noDataMessage={t('errorMessages.no-data-to-display')}
                    loading={intolerancesLoading}
                />
                <div className='nhp-right-col'>
                    <PieChartCard
                        title={capitalizeFirst(t('intolerances'))}
                        data={intolerancePieData}
                        isEmpty={intolerancePieIsEmpty}
                        loading={intolerancesLoading}
                    />
                </div>
            </div>

            {/* ── Charts row: illnesses distribution ── */}
            <div className='nhp-charts-row' data-tour="hp-illnesses-chart">
                <BarChartCard
                    title={capitalizeFirst(t('illnesses'))}
                    data={illnessesBarData}
                    xAxisLabels={illnessesBarLabels}
                    yAxisLabel={t('Clients')}
                    noDataMessage={t('errorMessages.no-data-to-display')}
                    loading={illnessesLoading}
                />
                <div className='nhp-right-col'>
                    <PieChartCard
                        title={capitalizeFirst(t('illnesses'))}
                        data={illnessPieData}
                        isEmpty={illnessPieIsEmpty}
                        loading={illnessesLoading}
                    />
                </div>
            </div>

            {/* ── Charts row: diets generated per month ── */}
            <div className='nhp-charts-row' data-tour="hp-diets-generated-chart">
                <BarChartCard
                    title={capitalizeFirst(t('diets-generated-per-month'))}
                    yearOptions={yearOptions}
                    selectedYear={dietsBarChartYearFilter}
                    onYearChange={handleDietsBarChartYearFilter}
                    data={dietsGeneratedPerMonth}
                    xAxisLabels={months}
                    yAxisLabel={t('Diets')}
                    noDataMessage={t('errorMessages.no-data-to-display')}
                    loading={dietsBarChartLoading}
                />
            </div>

            {/* ── Charts row: average diet duration per month ── */}
            <div className='nhp-charts-row' data-tour="hp-avg-diet-duration-chart">
                <BarChartCard
                    title={capitalizeFirst(t('average-diet-duration'))}
                    yearOptions={yearOptions}
                    selectedYear={avgDietDurationYearFilter}
                    onYearChange={handleAvgDietDurationYearFilter}
                    data={avgDietDurationByMonth ?? []}
                    xAxisLabels={months}
                    yAxisLabel={t('Weeks')}
                    noDataMessage={t('errorMessages.no-data-to-display')}
                    loading={avgDietDurationLoading}
                />
            </div>

            {/* ── Charts row: diets by caloric range ── */}
            <div className='nhp-charts-row' data-tour="hp-diets-caloric-chart">
                <BarChartCard
                    title={capitalizeFirst(t('diets-by-caloric-range'))}
                    data={dietsCaloricData}
                    xAxisLabels={CALORIC_BUCKETS}
                    yAxisLabel={t('Diets')}
                    noDataMessage={t('errorMessages.no-data-to-display')}
                    loading={dietsCaloricLoading}
                />
            </div>

        {/* Edit event modal */}
        {editEventOpen && (
            <CalendarEventModal
                setShowModal={setEditEventOpen}
                event={editEventData}
                onSubmit={fetchUpcomingEvents}
            />
        )}
        </div>
    )
};

export default HomePage;
